import React, { useState, useMemo } from 'react';
import { SlidersHorizontal, X, Star } from 'lucide-react';
import ProductCard from './ProductCard';
import { getSelectedVariant } from '../../utils/productHelpers';

export const STOREFRONT_CATEGORY_CONFIG = [
  { id: 'handbags', label: 'Handbags', enabled: true },
  { id: 'shoes', label: 'Shoes', enabled: false },
  { id: 'accessories', label: 'Accessories', enabled: false },
  { id: 'perfumes', label: 'Perfumes', enabled: false },
];

const renderStars = (rating) => (
  Array.from({ length: 5 }, (_, i) => (
    <Star key={i} className={`w-3.5 h-3.5 ${i < Math.floor(rating) ? 'text-brass-deep fill-current' : 'text-line'}`} />
  ))
);

const ProductGrid = ({
  sectionRef,
  selectedStorefrontCategory,
  onSelectStorefrontCategory,
  processedCategories,
  selectedCategory,
  onSelectCategory,
  priceFilter,
  setPriceFilter,
  advancedFilters,
  setAdvancedFilters,
  availableBrands,
  availableMaterials,
  filteredProducts,
  animatingCartItem,
  showQuickAdd,
  onQuickView,
  onAddToCart,
  viewedProducts,
  showRecentlyViewed,
  setShowRecentlyViewed,
  recommendations,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const isCategoryEnabled = STOREFRONT_CATEGORY_CONFIG.find((c) => c.id === selectedStorefrontCategory)?.enabled;

  const DEFAULT_FILTERS = { brand: 'all', material: 'all', minRating: 0, inStockOnly: false, sortBy: 'featured' };
  const PRICE_PRESETS = [
    { label: 'Under 2K', min: 0, max: 2000 },
    { label: '2K – 5K', min: 2000, max: 5000 },
    { label: '5K – 10K', min: 5000, max: 10000 },
    { label: '10K+', min: 10000, max: 10000 },
  ];

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (advancedFilters.brand !== 'all') chips.push({ key: 'brand', label: advancedFilters.brand, onRemove: () => setAdvancedFilters((f) => ({ ...f, brand: 'all' })) });
    if (advancedFilters.material !== 'all') chips.push({ key: 'material', label: advancedFilters.material, onRemove: () => setAdvancedFilters((f) => ({ ...f, material: 'all' })) });
    if (advancedFilters.minRating > 0) chips.push({ key: 'rating', label: `${advancedFilters.minRating}+ stars`, onRemove: () => setAdvancedFilters((f) => ({ ...f, minRating: 0 })) });
    if (advancedFilters.inStockOnly) chips.push({ key: 'stock', label: 'In stock only', onRemove: () => setAdvancedFilters((f) => ({ ...f, inStockOnly: false })) });
    if (priceFilter[0] > 0 || priceFilter[1] < 10000) chips.push({ key: 'price', label: `KSh ${priceFilter[0].toLocaleString()}–${priceFilter[1].toLocaleString()}`, onRemove: () => setPriceFilter([0, 10000]) });
    return chips;
  }, [advancedFilters, priceFilter, setAdvancedFilters, setPriceFilter]);

  return (
    <section ref={sectionRef} className="max-w-7xl mx-auto px-6 sm:px-8 md:px-10 py-20 sm:py-28">
      <div className="mb-12 max-w-2xl">
        <p className="dpc-label text-accent mb-3">The Collection</p>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-ink leading-tight">
          Carried every day, made to last.
        </h2>
      </div>

      {/* Storefront category toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {STOREFRONT_CATEGORY_CONFIG.map((cat) => (
          <button
            key={cat.id}
            onClick={() => cat.enabled && onSelectStorefrontCategory(cat.id)}
            disabled={!cat.enabled}
            className={`dpc-label px-4 py-2 rounded-full border transition-colors ${
              selectedStorefrontCategory === cat.id
                ? 'bg-ink text-paper border-ink'
                : cat.enabled
                  ? 'border-line text-ink-soft hover:border-ink hover:text-ink'
                  : 'border-line text-ink-faint/50 cursor-not-allowed'
            }`}
          >
            {cat.label}{!cat.enabled && ' (Soon)'}
          </button>
        ))}
      </div>

      {/* Category pills + filter toggle */}
      {isCategoryEnabled && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10 pb-6 border-b border-line">
          <div className="flex flex-wrap gap-2">
            {processedCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => onSelectCategory(cat.id)}
                className={`text-sm px-4 py-1.5 rounded-full transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-accent text-paper'
                    : 'bg-paper text-ink-soft hover:text-ink border border-line'
                }`}
              >
                {cat.name} <span className="opacity-60">({cat.count})</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setFiltersOpen((o) => !o)}
            className={`flex items-center gap-2 text-sm dpc-label px-3.5 py-2 rounded-full border transition-colors ${
              filtersOpen ? 'border-ink text-ink' : 'border-line text-ink-soft hover:border-ink hover:text-ink'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {activeFilterChips.length > 0 && (
              <span className="bg-accent text-paper text-[10px] font-bold rounded-full w-4.5 h-4.5 min-w-[18px] px-1 flex items-center justify-center">
                {activeFilterChips.length}
              </span>
            )}
          </button>
        </div>
      )}

      {isCategoryEnabled && activeFilterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 -mt-6 mb-8">
          {activeFilterChips.map((chip) => (
            <button
              key={chip.key}
              onClick={chip.onRemove}
              className="flex items-center gap-1.5 text-xs bg-accent-soft text-accent-deep px-3 py-1.5 rounded-full hover:bg-accent hover:text-paper transition-colors"
            >
              {chip.label}
              <X className="w-3 h-3" />
            </button>
          ))}
          <button
            onClick={() => { setAdvancedFilters(DEFAULT_FILTERS); setPriceFilter([0, 10000]); }}
            className="text-xs text-ink-faint hover:text-ink underline"
          >
            Clear all
          </button>
        </div>
      )}

      {filtersOpen && isCategoryEnabled && (
        <div className="mb-10 p-6 bg-surface border border-line rounded-md space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="dpc-label text-ink-faint block mb-2">Brand</label>
              <select
                value={advancedFilters.brand}
                onChange={(e) => setAdvancedFilters((f) => ({ ...f, brand: e.target.value }))}
                className="w-full border border-ink-faint rounded-sm px-3 py-2.5 text-sm bg-paper focus:ring-1 focus:ring-accent focus:border-accent"
              >
                <option value="all">All brands</option>
                {availableBrands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="dpc-label text-ink-faint block mb-2">Material</label>
              <select
                value={advancedFilters.material}
                onChange={(e) => setAdvancedFilters((f) => ({ ...f, material: e.target.value }))}
                className="w-full border border-ink-faint rounded-sm px-3 py-2.5 text-sm bg-paper focus:ring-1 focus:ring-accent focus:border-accent"
              >
                <option value="all">All materials</option>
                {availableMaterials.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="dpc-label text-ink-faint block mb-2">Sort by</label>
              <select
                value={advancedFilters.sortBy}
                onChange={(e) => setAdvancedFilters((f) => ({ ...f, sortBy: e.target.value }))}
                className="w-full border border-ink-faint rounded-sm px-3 py-2.5 text-sm bg-paper focus:ring-1 focus:ring-accent focus:border-accent"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: low to high</option>
                <option value="price-high">Price: high to low</option>
                <option value="rating">Top rated</option>
              </select>
            </div>
          </div>

          <div>
            <label className="dpc-label text-ink-faint block mb-2">Price</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRICE_PRESETS.map((preset) => {
                const isActive = priceFilter[0] === preset.min && priceFilter[1] === preset.max;
                return (
                  <button
                    key={preset.label}
                    onClick={() => setPriceFilter(isActive ? [0, 10000] : [preset.min, preset.max])}
                    className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${
                      isActive ? 'bg-ink text-paper border-ink' : 'border-line text-ink-soft hover:border-ink hover:text-ink'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3 text-sm text-ink-soft">
              <span className="w-20 shrink-0">KSh {priceFilter[0].toLocaleString()}</span>
              <input
                type="range"
                min={0}
                max={10000}
                step={100}
                value={priceFilter[1]}
                onChange={(e) => setPriceFilter([priceFilter[0], Number(e.target.value)])}
                className="flex-1 accent-accent"
                aria-label="Maximum price"
              />
              <span className="w-20 shrink-0 text-right">KSh {priceFilter[1].toLocaleString()}</span>
            </div>
          </div>

          <div>
            <label className="dpc-label text-ink-faint block mb-2">Minimum rating</label>
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3, 4, 5].map((r) => (
                r === 0 ? (
                  <button
                    key="any"
                    onClick={() => setAdvancedFilters((f) => ({ ...f, minRating: 0 }))}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      advancedFilters.minRating === 0 ? 'bg-ink text-paper border-ink' : 'border-line text-ink-soft hover:border-ink'
                    }`}
                  >
                    Any
                  </button>
                ) : (
                  <button
                    key={r}
                    onClick={() => setAdvancedFilters((f) => ({ ...f, minRating: r }))}
                    aria-label={`${r} stars and up`}
                    className={`p-1.5 rounded-full transition-colors ${advancedFilters.minRating >= r ? 'text-brass-deep' : 'text-line hover:text-brass-deep/50'}`}
                  >
                    <Star className="w-5 h-5" fill={advancedFilters.minRating >= r ? 'currentColor' : 'none'} />
                  </button>
                )
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-line">
            <label className="flex items-center gap-2 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={advancedFilters.inStockOnly}
                onChange={(e) => setAdvancedFilters((f) => ({ ...f, inStockOnly: e.target.checked }))}
                className="w-4 h-4 rounded border-ink-faint accent-accent"
              />
              In stock only
            </label>
            <button
              onClick={() => { setAdvancedFilters(DEFAULT_FILTERS); setPriceFilter([0, 10000]); }}
              className="text-sm text-accent hover:text-accent-deep dpc-label"
            >
              Reset all
            </button>
          </div>
        </div>
      )}

      {/* Grid */}
      {!isCategoryEnabled ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/5] bg-surface rounded-sm mb-3" />
              <div className="h-3 bg-surface rounded w-1/2 mb-2" />
              <div className="h-4 bg-surface rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {filteredProducts.map((productGroup) => {
            const groupId = productGroup.productId || productGroup.id;
            const isJustAdded = Boolean(showQuickAdd) && String(showQuickAdd).startsWith(groupId);
            return (
              <ProductCard
                key={productGroup.id}
                productGroup={productGroup}
                isJustAdded={isJustAdded}
                onQuickView={onQuickView}
                onAddToCart={onAddToCart}
              />
            );
          })}
        </div>
      )}

      {filteredProducts.length === 0 && isCategoryEnabled && (
        <p className="text-center text-ink-faint py-16">No pieces match those filters yet.</p>
      )}

      {/* Recently viewed */}
      {viewedProducts.length > 0 && (
        <div className="mt-24 pt-12 border-t border-line">
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-display text-2xl text-ink">Recently viewed</h3>
            <button onClick={() => setShowRecentlyViewed((s) => !s)} className="text-sm text-ink-soft hover:text-ink dpc-label">
              {showRecentlyViewed ? 'Show less' : 'Show all'}
            </button>
          </div>
          <div className={`grid gap-5 ${showRecentlyViewed ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-8'}`}>
            {(showRecentlyViewed ? viewedProducts : viewedProducts.slice(0, 8)).map((productGroup) => (
              <ProductCard
                key={`recent-${productGroup.id}`}
                productGroup={productGroup}
                size="compact"
                onQuickView={onQuickView}
                onAddToCart={onAddToCart}
              />
            ))}
          </div>
        </div>
      )}

      {/* Trending */}
      {(recommendations.length > 0 || filteredProducts.length > 0) && (
        <div className="mt-24 pt-12 border-t border-line">
          <h3 className="font-display text-2xl text-ink mb-8">Trending now</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            {(recommendations.length > 0 ? recommendations : filteredProducts).slice(0, 4).map((productGroup) => {
              const variant = getSelectedVariant(productGroup);
              return (
                <button key={`trend-${productGroup.id}`} onClick={() => onQuickView(productGroup)} className="text-left group">
                  <div className="aspect-square overflow-hidden bg-surface rounded-sm mb-2">
                    <img
                      src={variant.image}
                      alt={productGroup.baseName || productGroup.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.src = '/images/placeholder.jpg'; }}
                    />
                  </div>
                  <p className="text-sm text-ink truncate">{productGroup.baseName || productGroup.name}</p>
                  <div className="flex items-center gap-1">{renderStars(variant.rating || 0)}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export const QuickAddToast = ({ visible, onViewCart }) => (
  <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] transition-all duration-300 ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'}`}>
    <div className="bg-ink text-paper dpc-label pl-5 pr-2 py-2 rounded-full shadow-xl flex items-center gap-3">
      Added to your bag
      <button
        onClick={onViewCart}
        className="bg-paper text-ink dpc-label text-[11px] px-4 py-2 rounded-full hover:bg-accent hover:text-paper transition-colors"
      >
        View Bag
      </button>
    </div>
  </div>
);

export const ImageZoomModal = ({ image, onClose }) => {
  if (!image) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-ink/90 flex items-center justify-center p-6" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 p-2 text-paper/80 hover:text-paper" aria-label="Close">
        <X className="w-7 h-7" />
      </button>
      <img src={image} alt="" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

export default ProductGrid;
